import React, { useState } from "react";
import { CardGenre, Card as CardType } from "../types";

interface CardProps {
  card: CardType;
  width?: number;
  height?: number;
}

const checkImage = async (path: string): Promise<boolean> => {
  try {
    const img = new Image();
    img.src = path;
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
    return true;
  } catch {
    return false;
  }
};
const Card: React.FC<CardProps> = ({ card, width = 300, height = 450 }) => {
  const [showAnswer, setShowAnswer] = useState(false);

  React.useEffect(() => {
    setShowAnswer(false);
  }, [card]);

  const { genre, penaltyCost, activity, quote, description } = card;
  // Penalty image path (e.g., Shot2.png)
  const penaltyImg = `${process.env.PUBLIC_URL}/images/general/shots/shot${penaltyCost}.png`;

  const genreColor: Record<CardGenre, string> = {
    truth: "#442195",
    dare: "#FF6B6B",
    gamble: "#4ECDC4",
    curse: "#FF4242",
    question: "#4CAF50",
    wildcard: "#9C27B0",
    wyr: "#607D8B",
    battle: "#442195",
    chaos: "#390E0E",
  };
  const genreText: Record<CardGenre, string> = {
    truth: "Truth",
    dare: "Dare",
    gamble: "Gamble",
    curse: "Curse",
    question: "Question",
    wildcard: "Wildcard",
    wyr: "Would you rather?",
    battle: "Battle",
    chaos: "Chaos",
  };

  const hasAnswer = description.includes("(Answer: ");
  const [question, answer] = hasAnswer
    ? card.description.split("(Answer: ")
    : [card.description, null];
  const cleanAnswer = answer?.replace(")", "");

  const cardImage = `${process.env.PUBLIC_URL}/images/${genre}/${activity}/img.jpg`;
  const genreFallbackImage = `${process.env.PUBLIC_URL}/images/${genre}/fallback.jpg`;
  const defaultFallbackImage = `${process.env.PUBLIC_URL}/images/general/fallback.jpg`;

  const [imageExists, setImageExists] = useState(false);
  const [fallbackExists, setFallbackExists] = useState(false);

  React.useEffect(() => {
    const checkImages = async () => {
      const cardExists = await checkImage(cardImage);
      setImageExists(cardExists);
      if (!cardExists) {
        const fallbackExists = await checkImage(genreFallbackImage);
        setFallbackExists(fallbackExists);
      }
    };
    checkImages();
  }, [cardImage, genreFallbackImage]);

  const image = imageExists
    ? cardImage
    : fallbackExists
    ? genreFallbackImage
    : defaultFallbackImage;

  return (
    <div
      className={`rounded-3xl border-2 border-[#fff]  bg-white flex flex-col overflow-hidden shadow-xl relative select-none`}
      style={{
        // backgroundImage: `url(${process.env.PUBLIC_URL}/images/general/cardbackground.jpg)`,
        // backgroundSize: "cover",
        // backgroundPosition: "center",
        backgroundColor: "#fff",
        width: `${width}px`,
        height: `${height}px`,
      }}
    >
      <div
        className="flex items-center px-4 py-1 pt-2 relative"
        style={{ backgroundColor: genreColor[genre] }}
      >
        <span className="text-white text-md font-bold tracking-wide uppercase">
          {genreText[genre]}
        </span>
        {penaltyCost ? (
          <div className="absolute top-4 right-3 flex items-center z-[1]">
            <img
              src={penaltyImg}
              alt={`Penalty ${penaltyCost}`}
              className="w-16 h-16"
            />
            <span className="absolute right-3 top-2 text-white text-lg font-extrabold drop-shadow-lg">
              {card.penaltyCost}
            </span>
          </div>
        ) : (
          <></>
        )}
      </div>
      {/* Image */}
      <div className="h-[33%] flex items-center justify-center border-b border-black bg-black/40 relative">
        {cardImage ? (
          <img src={image} alt={genre} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500">DrinkUp</span>
          </div>
        )}
        {quote ? (
          <div className="border-b border-black px-4 py-2 font-bold text-white bg-[#222]/60 backdrop-blur-sm absolute bottom-0 left-0 right-0 text-center text-[0.8rem] italic">
            "{quote}"
          </div>
        ) : (
          <></>
        )}
      </div>
      {/* Title */}

      {/* Description/body */}
      <div className="flex-1 px-6 py-4 text-white bg-[#222] flex flex-col items-center justify-center text-center h-[120px] text-[1.3rem]">
        {question}
        {hasAnswer && (
          <div className="mt-4">
            <button
              onClick={() => setShowAnswer(!showAnswer)}
              className="text-sm px-3 py-1 rounded bg-[#442195] hover:bg-[#562bb5] transition-colors"
            >
              {showAnswer ? "Hide Answer" : "Show Answer"}
            </button>
            {showAnswer && (
              <p className="mt-2 text-md font-semibold text-yellow-400">
                {cleanAnswer}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Card;
